/**
 * The Share button in the corner of the host screen (SPEC §5.1) — AirDrop,
 * iMessage, WhatsApp, for the person who is not at the table. Falls back to the
 * clipboard where the Web Share API is missing (most desktop browsers).
 */

export type ShareOutcome = 'shared' | 'copied' | 'cancelled' | 'unavailable';

export interface ShareRoomInput {
  roomCode: string;
  url: string;
}

export function canWebShare(): boolean {
  return typeof navigator !== 'undefined' && typeof navigator.share === 'function';
}

export async function shareRoom({ roomCode, url }: ShareRoomInput): Promise<ShareOutcome> {
  const payload = {
    title: 'The ID Game',
    text: `Join my game — room ${roomCode}`,
    url,
  };

  if (canWebShare()) {
    try {
      await navigator.share(payload);
      return 'shared';
    } catch (error) {
      // AbortError is the user dismissing the sheet — not a failure.
      if ((error as { name?: string } | null)?.name === 'AbortError') return 'cancelled';
      // Anything else: fall through to the clipboard.
    }
  }

  return (await copyToClipboard(url)) ? 'copied' : 'unavailable';
}

export async function copyToClipboard(text: string): Promise<boolean> {
  try {
    if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    /* fall through */
  }

  // Legacy path: still the only thing that works in some in-app webviews.
  try {
    const area = document.createElement('textarea');
    area.value = text;
    area.setAttribute('readonly', '');
    area.style.position = 'fixed';
    area.style.opacity = '0';
    document.body.appendChild(area);
    area.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(area);
    return ok;
  } catch {
    return false;
  }
}
